<template>
  <div
    ref="mapContainerRef"
    class="map-container"
    @mousedown.prevent="panStart"
    @mousemove.prevent="panMove"
    @mouseup.prevent="panEnd"
    @mouseleave.prevent="panEnd"
    @wheel.prevent="handleZoom"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchEnd"
  >
    <button
      type="button"
      class="recenter-btn"
      title="Recenter Map"
      @mousedown.stop
      @touchstart.stop
      @click="recenter"
    >
      <i class="fas fa-crosshairs"></i>
    </button>

    <div
      class="map-world"
      :style="{
        transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
      }"
    >
      <svg class="svg-layer">
        <defs>
          <filter
            id="ethereal-glow"
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <g class="lines-group">
          <line
            v-for="(line, index) in dependencyLines"
            :key="index"
            :x1="line.x1"
            :y1="line.y1"
            :x2="line.x2"
            :y2="line.y2"
            :class="['dependency-line', `line-${line.status}`]"
          />
        </g>
      </svg>

      <div
        v-for="node in progressionData.nodes"
        :key="node.id"
        class="map-node"
        :class="[
          node.node_type,
          nodeStatus[node.id], // The status class (activated, available, disabled)
          { selected: selectedNodeId === node.id },
          { 'has-card-art': !!socketArtFor(node)?.image },
        ]"
        :style="{ left: `${node.position.x}px`, top: `${node.position.y}px` }"
        @click.stop="selectNode(node)"
      >
        <div
          v-if="socketArtFor(node)?.image"
          class="node-art"
          :style="{ backgroundImage: `url('${socketArtFor(node).image}')` }"
        ></div>
        <div class="node-icon">
          <i
            v-if="node.node_type === 'socket'"
            class="fas fa-drafting-compass"
          ></i>
          <i v-else-if="node.node_type === 'stat'" class="fas fa-star"></i>
        </div>
        <div class="node-label">
          {{ getNodeLabel(node) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";

const props = defineProps({
  progressionData: {
    type: Object,
    required: true,
  },
  playerRace: { type: String, default: "Eldorians" },
  socketArtByNode: {
    type: Object,
    default: () => ({}),
  },
});

watch(
  () => props.progressionData,
  (newData, oldData) => {
    console.log("--- ProgressionMap.vue: progressionData prop updated ---");
    // Use JSON.stringify to deep-copy and log the plain object,
    // preventing issues with Vue Proxies in the console.
    console.log("New Data:", JSON.parse(JSON.stringify(newData)));
    // You can optionally log oldData too if needed for comparison
    // console.log("Old Data:", JSON.parse(JSON.stringify(oldData)));
  },
  { deep: true, immediate: true }
); // 'deep' watches nested changes, 'immediate' runs on load

const emit = defineEmits(["node-selected"]);

// --- State ---
const mapContainerRef = ref(null); // Ref for the container div
const zoomLevel = ref(0.8); // Start a bit zoomed out
const minZoom = 0.2;
const maxZoom = 2.0;
const panOffset = ref({ x: 0, y: 0 });
const isPanning = ref(false);
const panStartPos = ref({ x: 0, y: 0 });
const selectedNodeId = ref(null);
const TOUCH_SLOP = 6;
const lastTouch = ref({ x: 0, y: 0 });
const touchOrigin = ref({ x: 0, y: 0 });
const didPan = ref(false);
const isTouchPanning = ref(false);
const isPinching = ref(false);
const pinchStartDist = ref(0);
const pinchStartZoom = ref(1);

function clampZoom(value) {
  return Math.min(maxZoom, Math.max(minZoom, value));
}

function touchDistance(t1, t2) {
  return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
}

function applyZoomAt(clientX, clientY, nextZoom) {
  const el = mapContainerRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  const worldX = (x - panOffset.value.x) / zoomLevel.value;
  const worldY = (y - panOffset.value.y) / zoomLevel.value;
  panOffset.value = {
    x: x - worldX * nextZoom,
    y: y - worldY * nextZoom,
  };
  zoomLevel.value = nextZoom;
}

function beginPinch(event) {
  isPinching.value = true;
  isTouchPanning.value = false;
  isPanning.value = false;
  didPan.value = true;
  pinchStartDist.value = touchDistance(event.touches[0], event.touches[1]);
  pinchStartZoom.value = zoomLevel.value;
}

function clearPinch() {
  isPinching.value = false;
  pinchStartDist.value = 0;
}

function isCompatMouseFromTouch(event) {
  return event.sourceCapabilities?.firesTouchEvents === true;
}

// --- Panning ---
const panStart = (event) => {
  if (isCompatMouseFromTouch(event)) return;
  didPan.value = false;
  isPanning.value = true;
  panStartPos.value = {
    x: event.clientX - panOffset.value.x,
    y: event.clientY - panOffset.value.y,
  };
};

const panMove = (event) => {
  if (isCompatMouseFromTouch(event) || !isPanning.value) return;
  panOffset.value = {
    x: event.clientX - panStartPos.value.x,
    y: event.clientY - panStartPos.value.y,
  };
};

const panEnd = () => {
  isPanning.value = false;
};

// --- Zooming ---
const handleZoom = (event) => {
  const zoomFactor = 0.1;
  const zoomDirection = event.deltaY < 0 ? 1 : -1;
  const newZoom = zoomLevel.value + zoomFactor * zoomDirection;

  if (newZoom < minZoom || newZoom > maxZoom) return;
  applyZoomAt(event.clientX, event.clientY, newZoom);
};

const onTouchStart = (event) => {
  didPan.value = false;
  if (event.touches.length === 2) {
    event.preventDefault();
    beginPinch(event);
    return;
  }
  if (event.touches.length !== 1) return;
  const t = event.touches[0];
  touchOrigin.value = { x: t.clientX, y: t.clientY };
  lastTouch.value = { x: t.clientX, y: t.clientY };
  isTouchPanning.value = true;
  isPinching.value = false;
};

const onTouchMove = (event) => {
  if (event.touches.length === 2) {
    event.preventDefault();
    if (!isPinching.value) beginPinch(event);
    const dist = touchDistance(event.touches[0], event.touches[1]);
    if (pinchStartDist.value < 1) return;
    const next = clampZoom(
      pinchStartZoom.value * (dist / pinchStartDist.value)
    );
    const midX =
      (event.touches[0].clientX + event.touches[1].clientX) / 2;
    const midY =
      (event.touches[0].clientY + event.touches[1].clientY) / 2;
    applyZoomAt(midX, midY, next);
    return;
  }
  if (
    event.touches.length !== 1 ||
    !isTouchPanning.value ||
    isPinching.value
  ) {
    return;
  }
  const t = event.touches[0];
  const dx = t.clientX - touchOrigin.value.x;
  const dy = t.clientY - touchOrigin.value.y;
  if (Math.hypot(dx, dy) > TOUCH_SLOP) {
    didPan.value = true;
  }
  if (didPan.value) {
    event.preventDefault();
    panOffset.value = {
      x: panOffset.value.x + (t.clientX - lastTouch.value.x),
      y: panOffset.value.y + (t.clientY - lastTouch.value.y),
    };
  }
  lastTouch.value = { x: t.clientX, y: t.clientY };
};

const onTouchEnd = (event) => {
  if (event.touches.length < 2) {
    clearPinch();
  }
  // Leftover finger after a pinch must not pan until a fresh touchstart.
  isTouchPanning.value = false;
};

// --- Helpers ---
const selectNode = (node) => {
  if (didPan.value) return;
  const status = nodeStatus.value[node.id];
  if (status === "disabled") {
    selectedNodeId.value = null;
    emit("node-selected", null);
    return;
  }

  selectedNodeId.value = node.id;
  emit("node-selected", { node, status });
};

const getNodeLabel = (node) => {
  return node.labels[props.playerRace]?.label || node.node_key || node.id;
};

const socketArtFor = (node) => {
  const key = node?.node_key || node?.id;
  if (!key) return null;
  return props.socketArtByNode[key] || null;
};

// --- Computed Status Logic (Unchanged) ---
const nodesById = computed(() => {
  return props.progressionData.nodes.reduce((acc, node) => {
    acc[node.id] = node;
    return acc;
  }, {});
});

const destinationNodes = computed(() => {
  return new Set(props.progressionData.dependencies.map((dep) => dep.to));
});

const parentNodesMap = computed(() => {
  return props.progressionData.dependencies.reduce((acc, dep) => {
    if (!acc[dep.to]) {
      acc[dep.to] = [];
    }
    acc[dep.to].push(dep.from);
    return acc;
  }, {});
});

const nodeStatus = computed(() => {
  const statuses = {};
  const isRootNode = (nodeId) => !destinationNodes.value.has(nodeId);
  const hasActivatedParent = (nodeId) => {
    const parents = parentNodesMap.value[nodeId];
    if (!parents) return false;
    return parents.some((parentId) => statuses[parentId] === "activated");
  };

  for (const node of props.progressionData.nodes) {
    if (node.isUnlocked) {
      statuses[node.id] = "activated";
    }
  }
  for (const node of props.progressionData.nodes) {
    if (statuses[node.id] === "activated") continue;
    if (isRootNode(node.id) || hasActivatedParent(node.id)) {
      statuses[node.id] = "available";
    } else {
      statuses[node.id] = "disabled";
    }
  }
  return statuses;
});

const dependencyLines = computed(() => {
  const currentStatuses = nodeStatus.value; // Get the status map

  console.log(
    "ProgressionMap: Recalculating lines using statuses:",
    currentStatuses
  );

  return props.progressionData.dependencies
    .map((dep) => {
      const fromNode = nodesById.value[dep.from];
      const toNode = nodesById.value[dep.to];
      if (!fromNode || !toNode) return null;

      const fromStatus = currentStatuses[dep.from];
      const toStatus = currentStatuses[dep.to];

      console.log(
        `ProgressionMap: Line ${dep.from} -> ${dep.to}. Statuses: ${fromStatus} -> ${toStatus}`
      );

      // --- CORRECTED LOGIC ---
      let lineStatus = "disabled"; // Default: gray dashed
      if (fromStatus === "activated") {
        if (toStatus === "activated") {
          lineStatus = "activated"; // Both active: cyan glow
        } else {
          // Includes toStatus === 'available' or 'disabled' initially
          lineStatus = "available"; // From is active, To is not: white solid
        }
      }
      // --- END CORRECTION ---

      return {
        x1: fromNode.position.x + 50,
        y1: fromNode.position.y + 50,
        x2: toNode.position.x + 50,
        y2: toNode.position.y + 50,
        status: lineStatus,
      };
    })
    .filter((line) => line !== null);
});
watch(dependencyLines, (newLines) => {
  console.log("ProgressionMap: Dependency lines updated!", newLines);
});

// --- NEW: Auto-Centering Logic ---

// This function calculates the correct pan/zoom
function autoCenterMap() {
  if (!mapContainerRef.value || !props.progressionData.nodes.length) return;

  // 1. Find the root node (first 'available' one)
  const rootNode = props.progressionData.nodes.find(
    (node) => nodeStatus.value[node.id] === "available"
  );

  // Fallback: just use the first node
  const targetNode = rootNode || props.progressionData.nodes[0];

  if (!targetNode) return; // No nodes at all

  const { clientWidth, clientHeight } = mapContainerRef.value;

  console.log(`--- ProgressionMap.vue ---`);
  console.log(
    `Centering on: ${targetNode.id} at [${targetNode.position.x}, ${targetNode.position.y}]`
  );
  console.log(`Container size: ${clientWidth}w x ${clientHeight}h`);

  const newZoom = 0.8;
  zoomLevel.value = newZoom;

  // 3. Calculate pan to center the target node
  // Pan = (ScreenCenter) - (Node's position * zoom)
  panOffset.value = {
    x: clientWidth / 2 - targetNode.position.x * newZoom - 50 * newZoom, // 50 = half node width
    y: clientHeight / 2 - targetNode.position.y * newZoom - 50 * newZoom, // 50 = half node height
  };
}

// Recenter button also uses the new logic
const recenter = () => {
  autoCenterMap();
};

// Watch for the data to load, then center the map
watch(
  () => props.progressionData,
  (newData) => {
    if (newData && newData.nodes.length > 0 && mapContainerRef.value) {
      autoCenterMap();
    }
  },
  { immediate: true }
); // 'immediate' tries to run, but will re-run onMounted

onMounted(() => {
  console.log("--- ProgressionMap.vue ---");
  console.log("Data received as prop:", props.progressionData);
  if (props.progressionData && props.progressionData.nodes.length > 0) {
    autoCenterMap();
  }
});

watch(
  nodeStatus,
  (newStatus) => {
    console.log("--- ProgressionMap.vue ---");
    console.log("Calculated Node Statuses:", newStatus);
  },
  { immediate: true }
);
</script>

<style scoped>
/* Ensure FontAwesome is loaded (already in AppFooter) */
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css");

.map-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  cursor: grab;
  background-color: transparent;
  touch-action: none;
  user-select: none;
}
.map-container:active {
  cursor: grabbing;
}

.map-world {
  width: 1px; /* The world itself is just a container */
  height: 1px;
  transform-origin: 0 0;
  will-change: transform;
}

.recenter-btn {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  padding: 0;
  border-radius: 50%;
  box-shadow: 0 0 15px rgba(0, 246, 255, 0.5);
  background-color: rgba(36, 40, 59, 0.9);
  border: 2px solid #00f6ff;
  color: #00f6ff;
  cursor: pointer;
  transition: all 0.3s ease;
}

.recenter-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 0 20px rgba(0, 246, 255, 0.8);
}

/* --- SVG Lines --- */
.svg-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 4000px; /* Big enough to contain the map */
  height: 3000px;
  pointer-events: none;
  overflow: visible;
}

/* Base style for all lines */
.dependency-line {
  stroke-width: 4;
  transition: all 0.4s ease;
}

/* 1. Disabled Line (Default) */
.dependency-line.line-disabled {
  stroke: #414868; /* Dark Gray */
  stroke-dasharray: 8 4; /* Dashed */
}

/* 2. Available Line (White) */
.dependency-line.line-available {
  stroke: #c0caf5; /* White-ish */
  stroke-dasharray: 0; /* Solid */
}

/* 3. Activated Line (Cyan Glow) */
.dependency-line.line-activated {
  stroke: #00f6ff; /* Cyan */
  stroke-dasharray: 0; /* Solid */
  /*filter: url(#ethereal-glow); /* Apply the glow defined in <defs> */
}

/* --- Map Nodes --- */
.map-node {
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 3px solid;
  background-color: #24283b;
  backdrop-filter: blur(4px);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 8px;
  box-sizing: border-box;
  will-change: transform, box-shadow;
}

.node-icon {
  font-size: 2rem;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.node-label {
  font-size: 0.8rem;
  color: #a9b1d6;
  font-family: "Exo 2", sans-serif;
  font-weight: bold;
  line-height: 1.2;
  position: relative;
  z-index: 1;
}

.node-art {
  position: absolute;
  inset: 3px;
  background-size: cover;
  background-position: center;
  opacity: 0.62;
  pointer-events: none;
  border-radius: 50%;
}

.map-node.socket .node-art {
  border-radius: 17px;
}

.map-node.has-card-art::after {
  content: "";
  position: absolute;
  inset: 3px;
  border-radius: inherit;
  background: linear-gradient(
    180deg,
    rgba(10, 12, 24, 0.12) 18%,
    rgba(10, 12, 24, 0.72) 100%
  );
  pointer-events: none;
}

.map-node.has-card-art .node-icon {
  display: none;
}

.map-node.has-card-art .node-label {
  margin-top: auto;
  color: #e7ecf1;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.9);
}

/* --- Node Types: Socket vs Stat --- */
.map-node.socket {
  border-radius: 20px; /* More of a square */
}

/* --- Node States --- */
@keyframes pulse-available {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  70% {
    box-shadow: 0 0 10px 15px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

@keyframes pulse-activated {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 246, 255, 0.7);
  }
  70% {
    box-shadow: 0 0 10px 15px rgba(0, 246, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 246, 255, 0);
  }
}

/* 1. DISABLED (Default) */
.map-node.disabled {
  border-color: #414868;
  background-color: rgba(36, 40, 59, 0.6);
  cursor: not-allowed;
  opacity: 1;
}
.map-node.disabled .node-icon {
  color: #414868;
}
.map-node.disabled .node-label {
  color: #7a82a7;
}

/* 2. AVAILABLE */
.map-node.available {
  border-color: #c0caf5; /* White-ish border */
  background-color: rgba(169, 177, 214, 0.1);
  animation: pulse-available 2s infinite;
}
.map-node.available:hover {
  transform: scale(1.1);
  border-color: #ff00ff; /* Pink on hover */
}
.map-node.available .node-icon {
  color: #c0caf5;
}

/* 3. ACTIVATED */
.map-node.activated {
  border-color: #00f6ff;
  background-color: rgba(0, 246, 255, 0.1);
  box-shadow: 0 0 15px rgba(0, 246, 255, 0.5),
    inset 0 0 10px rgba(0, 246, 255, 0.3);
  animation: pulse-activated 2.5s infinite;
}
.map-node.activated .node-icon {
  color: #00f6ff;
  text-shadow: 0 0 10px #00f6ff;
}

/* 4. SELECTED */
.map-node.selected {
  /* Selected overrides other states */
  transform: scale(1.15) !important;
  animation-play-state: paused !important; /* Pause pulse when selected */
}
.map-node.available.selected {
  border-color: #ff00ff;
  box-shadow: 0 0 25px rgba(255, 0, 255, 0.8),
    inset 0 0 15px rgba(255, 0, 255, 0.5);
}
.map-node.activated.selected {
  border-color: #ff00ff;
  box-shadow: 0 0 25px rgba(255, 0, 255, 0.8),
    inset 0 0 15px rgba(255, 0, 255, 0.5);
}
</style>
